import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { App } from 'projects/internal/r1-component-library/src/lib/models/appcloud/app.model';
import { Account } from 'projects/internal/r1-component-library/src/lib/components/appcloud-login/appcloud-credentials/appcloud-credentials.model';
import { SubscriptionsService } from '../services/subscriptions.service';
import { ManageAccountsService } from '../services/manage-accounts.service';
import { isSubscriptionExpired, Subscription, SubscriptionPatch } from '../models/subscription.model';

export type AppsState = {
    account: Account | null;
    subscriptions: Subscription[];
    apps: App[];
    selectedSubscriptionId: number | null;
    loading: boolean;
    saving: boolean;
};

const initialState: AppsState = {
    account: null,
    subscriptions: [],
    apps: [],
    selectedSubscriptionId: null,
    loading: false,
    saving: false,
};

/**
 * "Apps" tool page: subscriptions of one account (active / expired), recommended apps and
 * the single "App Page". Provided in the `tools/apps` route so list and detail share it.
 */
export const AppsStore = signalStore(
    withState(initialState),
    withProps(() => ({
        _subscriptionsService: inject(SubscriptionsService),
        _accountsService: inject(ManageAccountsService),
    })),
    withComputed(store => ({
        hasAccount: computed(() => store.account() !== null),
        activeSubscriptions: computed(() => store.subscriptions().filter(s => !isSubscriptionExpired(s))),
        expiredSubscriptions: computed(() => store.subscriptions().filter(s => isSubscriptionExpired(s))),
        /** Installable apps the account has no subscription for */
        recommendedApps: computed(() => {
            const licensedAppIds = new Set(store.subscriptions().map(s => s.appId ?? s.app?.id));
            return store.apps().filter(app => app.installable !== false && !licensedAppIds.has(app.id));
        }),
        selectedSubscription: computed(
            () => store.subscriptions().find(s => s.id === store.selectedSubscriptionId()) ?? null
        ),
    })),
    withMethods(store => {
        const _replace = (updated: Subscription) =>
            patchState(store, {
                subscriptions: store.subscriptions().map(s => (s.id === updated.id ? { ...s, ...updated } : s)),
            });

        const loadSubscriptions = rxMethod<number | null>(
            switchMap(accountId => {
                if (accountId === null) {
                    patchState(store, { subscriptions: [] });
                    return of(null);
                }
                patchState(store, { loading: true });
                return store._subscriptionsService.findByAccount(accountId).pipe(
                    tapResponse({
                        next: subscriptions => patchState(store, { subscriptions: subscriptions ?? [] }),
                        error: () => patchState(store, { subscriptions: [] }),
                        finalize: () => patchState(store, { loading: false }),
                    })
                );
            })
        );

        return {
            loadApps: rxMethod<void>(
                switchMap(() =>
                    store._subscriptionsService.findApps().pipe(
                        tapResponse({
                            next: apps => patchState(store, { apps: apps ?? [] }),
                            error: () => patchState(store, { apps: [] }),
                        })
                    )
                )
            ),
            selectAccount(account: Account | null) {
                patchState(store, { account, selectedSubscriptionId: null });
                loadSubscriptions(account?.id ?? null);
            },
            /** Deep link support: `tools/apps?accountId=…` */
            selectAccountById: rxMethod<number | null>(
                switchMap(accountId => {
                    if (accountId === null || store.account()?.id === accountId) {
                        return of(null);
                    }
                    return store._accountsService.getAccount(accountId).pipe(
                        tapResponse({
                            next: account => {
                                patchState(store, { account });
                                loadSubscriptions(account.id ?? null);
                            },
                            error: () => undefined,
                        })
                    );
                })
            ),
            /** App Page: uses the loaded list when possible, otherwise fetches the single subscription. */
            selectSubscription: rxMethod<number>(
                switchMap(id => {
                    patchState(store, { selectedSubscriptionId: id });
                    if (store.subscriptions().some(s => s.id === id)) {
                        return of(null);
                    }
                    patchState(store, { loading: true });
                    return store._subscriptionsService.get(id).pipe(
                        tapResponse({
                            next: subscription => patchState(store, { subscriptions: [subscription] }),
                            error: () => undefined,
                            finalize: () => patchState(store, { loading: false }),
                        })
                    );
                })
            ),
            patchSubscription: rxMethod<{ id: number; patch: SubscriptionPatch }>(
                switchMap(({ id, patch }) => {
                    patchState(store, { saving: true });
                    return store._subscriptionsService.patch(id, patch).pipe(
                        tapResponse({
                            next: updated => _replace({ ...patch, ...updated, id }),
                            error: () => undefined,
                            finalize: () => patchState(store, { saving: false }),
                        })
                    );
                })
            ),
            renewSubscription: rxMethod<number>(
                switchMap(id => {
                    patchState(store, { saving: true });
                    return store._subscriptionsService.renew(id).pipe(
                        tapResponse({
                            next: renewed => _replace({ ...renewed, id }),
                            error: () => undefined,
                            finalize: () => patchState(store, { saving: false }),
                        })
                    );
                })
            ),
        };
    }),
    withHooks({
        onInit(store) {
            store.loadApps();
        },
    })
);
